import { PaymentAmountSection } from '@/src/app/main/(tabs)/card/card-management/_common/paymentAmountSection'
import { DatePicker } from '@/src/common/components/DatePicker'
import { fontFamilies } from '@/src/common/theme'
import type { AutoPayScheduleComponentType } from '@/src/experiences/card-payments/screens/AutoPaySchedule'
import {
  type RecurringPaymentStore,
  useRecurringPaymentStore,
} from '@/src/experiences/card-payments/store/useRecurringPaymentStore'
import { CardAccountTransferScheduleAmountData } from '@acre-rewards/lib'
import { type ComponentProps, useState } from 'react'
import { useEvent } from 'tamagui'
import { z } from 'zod/v4'

type CardAccountTransferScheduleAmountData = z.infer<typeof CardAccountTransferScheduleAmountData>

export type RecurringPaymentStrategy = RecurringPaymentStore['paymentStrategy']

const amountTypes = {
  min: { amountType: CardAccountTransferScheduleAmountData.options[0].shape.amountType.value },
  total: { amountType: CardAccountTransferScheduleAmountData.options[1].shape.amountType.value },
  statement: {
    amountType: CardAccountTransferScheduleAmountData.options[2].shape.amountType.value,
  },
  static: {
    amountType: CardAccountTransferScheduleAmountData.options[3].shape.amountType.value,
    amountCents: 0,
  },
} satisfies {
  [key in CardAccountTransferScheduleAmountData['amountType']]: RecurringPaymentStrategy
}

const amountTypesArray = Object.values(amountTypes)

export const getAmountTypeLabel = (({ amountType }) => {
  switch (amountType) {
    case 'min':
      return 'Minimum Payment'
    case 'total':
      return 'Current Balance'
    case 'statement':
      return 'Statement Balance'
    default:
      amountType satisfies 'static'
      return 'Other Amount'
  }
}) satisfies (
  paymentStrategy: RecurringPaymentStrategy,
) => ComponentProps<typeof PaymentAmountSection>['selectedPaymentType']

export function PlatformAutoPaySchedule({
  AutoPayScheduleComponent,
}: {
  AutoPayScheduleComponent: AutoPayScheduleComponentType
}) {
  const recurringPaymentStore = useRecurringPaymentStore()
  const [paymentStrategy, setPaymentStrategy] = useState<RecurringPaymentStrategy>(amountTypes.min)
  const [day, setDay] = useState<Date>()
  const [paymentAmount, setPaymentAmountUnbound] = useState<
    ComponentProps<typeof PaymentAmountSection>['paymentAmount']
  >(amountTypes.static.amountCents.toString())

  const setPaymentAmount = useEvent((newPaymentAmount: typeof paymentAmount) => {
    setPaymentAmountUnbound(newPaymentAmount)
    if (paymentStrategy.amountType === 'static') {
      setPaymentStrategy({ ...paymentStrategy, amountCents: Number(newPaymentAmount) })
    }
  })

  return (
    <AutoPayScheduleComponent
      recurringPaymentStore={recurringPaymentStore}
      paymentStrategies={amountTypesArray}
      paymentStrategy={paymentStrategy}
      setPaymentStrategy={(newPaymentStrategy) =>
        setPaymentStrategy(newPaymentStrategy || amountTypes.min)
      }
      day={day?.toISOString()}
      setDay={(isoDateString) => (isoDateString ? setDay(new Date(isoDateString)) : undefined)}
      shouldDisableContinueButton={
        paymentStrategy.amountType === 'static' && !Number(paymentAmount)
      }
      paymentStrategySelector={
        <PaymentAmountSection
          paymentAmount={
            paymentStrategy.amountType === 'static'
              ? paymentStrategy.amountCents.toString()
              : undefined
          }
          minimumPayment=""
          statementBalance=""
          currentBalance=""
          onAmountChange={setPaymentAmount}
          selectedPaymentType={getAmountTypeLabel(paymentStrategy)}
          onPaymentTypeSelectionChange={(paymentType) => {
            switch (paymentType) {
              case 'Minimum Payment':
                setPaymentStrategy(amountTypes.min)
                break
              case 'Current Balance':
                setPaymentStrategy(amountTypes.total)
                break
              case 'Statement Balance':
                setPaymentStrategy(amountTypes.statement)
                break
              default:
                paymentType satisfies 'Other Amount'
                setPaymentStrategy({
                  ...amountTypes.static,
                  amountCents: Number(paymentAmount),
                })
                break
            }
          }}
        />
      }
      daySelector={
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        <DatePicker
          date={day}
          onDateChange={setDay}
          inputProps={{
            style: {
              fontFamily: fontFamilies['PlusJakartaSans-Bold'],
              fontWeight: 700,
            },
          }}
          minimumDate={new Date()}
        />
      }
    />
  )
}
